/**
 * CredentialPrompt — credential form + picker for cred_to_env tool calls.
 *
 * Shows:
 * - Active credential label + pre-filled form
 * - Other saved credential tags at bottom for quick switch
 * - Dynamic buttons: Approve (unchanged) / Update + Save As (changed)
 * - Auto-resolve from keybook for returning users
 */
import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo, Fragment } from 'react'
import { BookKey, Check, Clock, Loader2, RefreshCw, Plus, Pencil, Key, Trash2, ExternalLink } from 'lucide-react'
import { CredentialPickerDialog } from './CredentialPickerDialog.js'
import { KeybookKeyCard } from './KeybookKeyCard.js'
import { PasswordInput, GithubIcon } from '@papan/core-ui/ui'
import { getApiBase, errorText } from '@papan/api-client'
import { useBrand } from '@papan/brand/react'
import { useTranslation } from '@papan/i18n'
import type { QuestionRequest } from './hooks.js'
import { erasedOwnerEmail } from '@papan/types'
import { entryFitsRequest, entryStoredFields, formValuesForEntry, storedValuesFromForm, requestedFieldClass, isBearerSplatType, presentFieldNames, isKnownCredentialType, classifyFieldName, matchCredentialFields, cardFieldMap } from '@papan/keybook/core/field-match'

interface CredentialField {
  name: string
  type: string
  env?: string
}

/**
 * What a card's answer carries beside its key (names and one-time values only — never a stored value):
 *  - #1441 `fieldMap`: the EXACT field map the card showed (requested field → stored field name). The binding and the
 *    answer resolve through it and are never re-matched, so a key whose fields change later never routes a value the
 *    card didn't show into a variable. Absent for a GitHub / PAT key (its one secret fills every field).
 *  - #1448 `oneTime`: the values typed into the request's `prompt` fields (one-time codes). Never written to the key or
 *    a binding: the answer hands them to an encrypted one-time slot for THIS turn only.
 */
export interface CredentialCardAnswer {
  fieldMap?: Record<string, string>
  oneTime?: Record<string, string>
}

interface Props {
  request: QuestionRequest
  fields?: CredentialField[]
  file?: string
  label?: string
  envMapping?: Record<string, string>
  force?: boolean
  onReply: (answers: string[][]) => void
  onDismiss: () => void
  /** Fired when the user submits the form. The form ALWAYS saves to
   *  the keybook first so an entryId is available — the per-turn
   *  resolver looks values up server-side from this entry, not from
   *  the browser. (entryId may be undefined only if save fails; the
   *  parent should treat that as an error.) */
  /** `saved` (I198 / D56): THIS submit created or updated the keybook entry and that
   *  write completed. False for an existing entry picked or auto-resolved. It decides
   *  whether an expired-question note may tell the user their key was saved. */
  onCredentialResolved?: (entryId?: string, saved?: boolean, credential?: CredentialCardAnswer) => void
  onFolderUpgrade?: (entryId: string) => void
  alreadyInSession?: boolean
  /** Edit mode — changes button labels to Save/Cancel */
  isEdit?: boolean
  /** Pre-fill values from session (skips keybook resolve) */
  initialValues?: Record<string, string>
  /** Entry ID for edit mode (sets activeEntry) */
  initialEntryId?: string
  /** The key's `version` at the read `initialValues` came from (#1324 owner review, I2). Update is sent against IT,
   *  never against the card's own later read: the form shows the first read's values, so a change made between the
   *  two reads must answer "changed" (409), not be overwritten by those values. */
  initialVersion?: number
  /** Called when user deletes the credential from session */
  onDelete?: () => void
  /** Service the agent is authenticating against (e.g. 'github').
   *  When set to 'github' AND no existing entry covers the request,
   *  the prompt swaps the generic field form for a 1-of-2 picker
   *  (Authorize on GitHub vs Personal Access Token) — same UX as
   *  EntryCreateDialog, just inline in chat. */
  provider?: string
  /** owner/name of the target repo, threaded through from the agent's
   *  context. Mostly informational for now; future use: prefer entries
   *  whose repo list covers it when surfacing tags. */
  contextRepo?: string
}

interface ResolvedEntry {
  fields: Record<string, string>
  entryId: string
  label?: string
  /** github / pat / password / api_key etc. — drives read-only
   *  rendering for token-bearing types where the value is server-
   *  managed (github OAuth) or the editable input would expose the
   *  secret in plaintext (pat). */
  credentialType?: string
  /** the entry's whole payload EXACTLY as read — never the form's fills (`fields` carries those). Update writes the
   *  form back onto its `fields` (`storedValuesFromForm`) and keeps its other keys (`usernameHint`…): the PATCH
   *  replaces the whole payload. Save As copies its `fields` (+ type, `fieldsMeta`) the same way. */
  storedData?: { fields?: Record<string, string> } & Record<string, unknown>
  fieldsMeta?: Record<string, { sensitive?: boolean }>
  fromSibling?: boolean
  siblingFileName?: string
  /** The keybook `version` the entry was read at — an update is sent against it
   *  (409 KEYBOOK_ENTRY_CHANGED when the key changed since). Absent until a read. */
  version?: number
}

/** The key's `version` from a keybook read, or undefined when the answer has
 *  none. Every read path goes through this: no version means nothing to save
 *  against, so Update stays disabled and the person is told — never a spinner
 *  forever, never an unconditional write. */
/** A form value by field name — an OWN string only, else ''. Field names come from the model: `constructor` /
 *  `__proto__` must never read Object's prototype (a function there crashed `.trim()` — #1324 owner review, Codex). */
function ownStr(o: Record<string, string>, k: string): string {
  const v = Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined
  return typeof v === 'string' ? v : ''
}

function versionOf(entry: unknown): number | undefined {
  const v = (entry as { version?: unknown } | null | undefined)?.version
  return typeof v === 'number' ? v : undefined
}

export function CredentialPrompt({
  request: _request,
  fields = [],
  file,
  label,
  envMapping = {},
  force,
  onReply,
  onDismiss: _onDismiss,
  onCredentialResolved,
  onFolderUpgrade,
  alreadyInSession,
  isEdit,
  initialValues,
  initialEntryId,
  initialVersion,
  onDelete,
  provider,
  contextRepo,
}: Props) {
  // Product name appears in the GitHub picker copy AND in the PAT deep-link's
  // pre-filled token name/description, which GitHub renders on its own page.
  const { productName } = useBrand()
  const { t } = useTranslation('keybook')
  const [values, setValues] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  /** An UPDATE of the selected key is in flight (only that — Save as / a new
   *  key don't hold the selection). */
  const updatingRef = useRef(false)
  /** A switch's read is in flight: Update waits for it (pressing Update on A
   *  while B loads would save A, then show B with no word about A). */
  const [switchPending, setSwitchPending] = useState(false)
  // A new key that failed to save: the card stays open, says so, and can be retried.
  const [saveError, setSaveError] = useState<string | null>(null)
  const [resolving, setResolving] = useState(true)
  const [activeEntry, setActiveEntry] = useState<ResolvedEntry | null>(null)
  const [originalValues, setOriginalValues] = useState<Record<string, string>>({})
  const [allEntries, setAllEntries] = useState<Array<{ id: string; label: string; owner?: string; provider?: string | null; fileId?: string | null; credentialType?: string; relevance: number; compatible: boolean }>>([])
  const [showAllEntries, setShowAllEntries] = useState(false)
  const [switchError, setSwitchError] = useState<string | null>(null)
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [isDraft, setIsDraft] = useState(false)
  const [draftLabel, setDraftLabel] = useState('')
  const draftValuesRef = useRef<Record<string, string>>({})
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const autoResolveAttempted = useRef(false)
  const resolvedFileId = useRef<string | null>(null)

  // ─── GitHub-flow state ──────────────────────────────────────────
  // When provider==='github' AND we're creating a new credential
  // (no activeEntry), the form swaps to a 1-of-2 picker mirroring
  // EntryCreateDialog. The mode below tracks which branch the user
  // is on; 'picker' is the initial choose-one screen.
  const [githubMode, setGithubMode] = useState<'picker' | 'authorize' | 'pat'>('picker')
  /** True between popup-open and BroadcastChannel 'installed' arriving.
   *  Drives the "Waiting for GitHub authorization…" state in the
   *  authorize panel so a user who closes the popup early sees a
   *  retry option instead of a frozen card. */
  const [authorizing, setAuthorizing] = useState(false)
  // Separate state for the PAT field — the agent's declared field
  // name is usually 'token', but EntryCreateDialog stores PATs under
  // the 'pat' key, so we keep the input value separate and only
  // splice it into the agent's field-name shape at submit time.
  const [patValue, setPatValue] = useState('')
  const [patValidating, setPatValidating] = useState(false)
  const [patValidation, setPatValidation] = useState<{
    accountLogin: string
    accountType: 'User' | 'Organization'
    repositoryNames: string[]
    repositoryCount: number
    truncated: boolean
    repositorySelection?: 'all' | 'selected'
    scope?: string
  } | null>(null)
  const [patError, setPatError] = useState<string | null>(null)
  const [updateError, setUpdateError] = useState<string | null>(null)
  /** The read that gives an existing key's version failed — Update stays disabled. */
  const [versionReadFailed, setVersionReadFailed] = useState(false)
  /** The last Update key was refused because the key changed since it was read
   *  (409) — Reload is offered next to the message. */
  const [updateChanged, setUpdateChanged] = useState(false)
  /** Bumped by every switch REQUEST: only the latest request's read may land
   *  (pick B then C — B's slower read must not replace C). */
  const switchGenRef = useRef(0)
  /** Bumped only when a switch LANDS (the selection really changed). The
   *  initial read and an update compare this one — a switch that FAILED leaves
   *  the selection, and so their results, valid. */
  const selectionGenRef = useRef(0)
  // Transient id that gets set whenever the user picks a different badge,
  // then cleared ~200ms later. The active badge renders at scale-105 while
  // this is set, then returns to scale-100 — giving a subtle pop.

  /** #1324: a `prompt` field (typed on the card each time) is never required of a saved key — but it is still
   *  MATCHED over, as the server matches every `envMapping` name (it changes what is unambiguous). */
  const promptFieldNames = (defs: CredentialField[]) => new Set(defs.filter((f) => f.type === 'prompt').map((f) => f.name))
  const fieldDefs = fields.length > 0 ? fields : [
    { name: 'username', type: 'text' },
    { name: 'password', type: 'password' },
  ]
  /** #1448: the request's `prompt` fields — a one-time code, typed for THIS answer: never written to a key, never
   *  filled from one (a code a key stored before #1448 is stale), never part of the field map. */
  const promptNames = promptFieldNames(fieldDefs)
  const blankPrompts = (v: Record<string, string>): Record<string, string> => {
    if (promptNames.size === 0) return v
    const out: Record<string, string> = {}
    for (const [k, val] of Object.entries(v)) if (!promptNames.has(k)) Object.defineProperty(out, k, { value: val, enumerable: true, writable: true, configurable: true })
    return out
  }
  /** #1448: the codes typed into the prompt fields, for this answer only. */
  const oneTimeValues = (): Record<string, string> | undefined => {
    const out: Record<string, string> = {}
    for (const n of promptNames) { const v = ownStr(values, n); if (v) Object.defineProperty(out, n, { value: v, enumerable: true, writable: true, configurable: true }) }
    return Object.keys(out).length > 0 ? out : undefined
  }
  /** #1441 / #1448: what the answer carries beside the key — the exact map of what the card showed (judged on the key
   *  as it stands after this submit's own write: `stored`), and the typed one-time codes. */
  const answerFor = (credentialType: string | undefined, stored: Record<string, string> | undefined, fieldsMeta?: Record<string, { sensitive?: boolean } | undefined>): CredentialCardAnswer => {
    const fieldMap = credentialType && stored ? cardFieldMap(requestedNames, credentialType, stored, values, fieldsMeta, envMapping, promptNames) : undefined
    const oneTime = oneTimeValues()
    return { ...(fieldMap ? { fieldMap } : {}), ...(oneTime ? { oneTime } : {}) }
  }
  /** The requested names the matcher runs over — each once, as the server sees them (`Object.keys(envMapping)`): a
   *  request repeating a name must not make the card and the server disagree (#1324 owner review, S4). */
  const requestedNames = [...new Set(fieldDefs.map((f) => f.name))]
  /** A field's env var name — an OWN string only (a model-named field `__proto__` / `constructor` never reads
   *  Object's prototype, which would crash the render — Codex). */
  const envNameOf = (n: string): string | undefined => {
    const v = Object.prototype.hasOwnProperty.call(envMapping, n) ? envMapping[n] : undefined
    return typeof v === 'string' ? v : undefined
  }
  /** Must a saved key supply this requested field? Judged with the env var it lands in (Codex #1, as the server). */
  const isRequiredSecretFor = (n: string) => {
    const c = requestedFieldClass(n, envNameOf(n))
    return c.kind === 'secret' && !c.optional
  }
  /** The requested fields the active key fills from a SECRET stored field (by its schema, its `fieldsMeta`, or its
   *  name) — masked whatever their own name says (#1324 owner review, S3: a `DATABASE_URL` from a field marked
   *  sensitive). A bearer key's card shows a read-only summary instead of inputs. */
  const secretSourced = (() => {
    const ct = activeEntry?.credentialType
    const loaded = activeEntry?.storedData?.fields
    if (!ct || !loaded || isBearerSplatType(ct)) return new Set<string>()
    const stored = entryStoredFields(ct, presentFieldNames(loaded), activeEntry?.fieldsMeta) ?? []
    const m = matchCredentialFields(requestedNames, stored, envMapping)
    return new Set(Object.keys(m).filter((r) => stored.find((s) => s.name === m[r])?.kind === 'secret'))
  })()

  /** GitHub-specific flow: provider is github, no existing entry has
   *  been picked, and we're not in edit mode. In this case the form
   *  swaps to the Authorize/PAT picker. The user can still pick an
   *  existing entry from the tags below — switchToEntry sets
   *  activeEntry, which short-circuits this flag. */
  const isGithubFlow = provider === 'github' && !activeEntry && !isEdit

  // Check if values have been modified from the saved entry
  const isModified = useMemo(() => {
    if (!activeEntry) return false
    return fieldDefs.some(f => {
      if (f.type === 'prompt') return false // OTP/prompt fields don't count
      return ownStr(values, f.name) !== ownStr(originalValues, f.name)
    })
  }, [values, originalValues, activeEntry, fieldDefs])

  // Auto-resolve from keybook (skip if initial values provided — edit mode)
  useEffect(() => {
    if (autoResolveAttempted.current) return
    autoResolveAttempted.current = true

    if (initialValues && Object.keys(initialValues).length > 0) {
      setValues(blankPrompts(initialValues))
      setOriginalValues(blankPrompts(initialValues))
      if (initialEntryId) {
        setActiveEntry({ fields: initialValues, entryId: initialEntryId, label: label })
        // The initial read is a "switch" too: any later switch supersedes it.
        const initialGen = selectionGenRef.current
        // Fetch the keybook entry's canonical label + credentialType
        // (async, best effort). credentialType drives the read-only
        // GitHub-authorized summary in place of the editable token
        // input. fields stay as initialValues — they were spliced at
        // session-bind time and persist through the session row.
        import('@papan/keybook/client').then(({ keybookApi }) => {
          keybookApi.getEntry(initialEntryId).then(({ entry }) => {
            // The person switched keys (even away and back) before this read came back.
            if (initialGen !== selectionGenRef.current) return
            // No version in the answer = nothing to save against: treated as a
            // failed read (never a spinner forever, never an unconditional write).
            if (versionOf(entry) === undefined) {
              setVersionReadFailed(true)
              setUpdateError('Failed to load credential')
            }
            // #1324: the entry's own field names, and the requested fields the matcher fills from them — only
            // where the form is still empty (never over what the person typed)
            const storedFields = (((entry as any)?.data?.fields) ?? {}) as Record<string, string>
            const storedMeta = (entry as any)?.data?.fieldsMeta as Record<string, { sensitive?: boolean }> | undefined
            const ct = entry?.credentialType
            if (ct) {
              const filled = blankPrompts(formValuesForEntry(requestedNames, ct, storedFields, storedMeta, envMapping))
              const fillEmpty = (prev: Record<string, string>) => {
                const next = { ...prev }
                for (const f of fieldDefs) if (!ownStr(next, f.name) && ownStr(filled, f.name)) Object.defineProperty(next, f.name, { value: ownStr(filled, f.name), enumerable: true, writable: true, configurable: true })
                return next
              }
              setValues(fillEmpty)
              setOriginalValues(fillEmpty)
            }
            setActiveEntry(prev => prev && prev.entryId === initialEntryId ? {
              ...prev,
              label: entry?.sourceLabel ?? prev.label,
              credentialType: entry?.credentialType ?? prev.credentialType,
              storedData: (entry as any)?.data ?? { fields: storedFields },
              fieldsMeta: storedMeta,
              // I2: the version the form's values were read at (the chat's read), when it says — else this read's
              version: initialVersion ?? versionOf(entry) ?? prev.version,
              // Carry the entry's plaintext fields too so the read-only
              // summary can show account_login, repository_count, etc.
              // initialValues was the session-bound subset (just the
              // bearer under each agent field name) — for the summary
              // we want the full installation metadata.
              fields: { ...(((entry as any).data?.fields) ?? {}), ...prev.fields },
            } : null)
          }).catch(() => {
            if (initialGen !== selectionGenRef.current) return
            // No version to save against: Update stays disabled (never an
            // unconditional overwrite), and the person is told.
            setVersionReadFailed(true)
            setUpdateError('Failed to load credential')
          })
        }).catch(() => {
          if (initialGen !== selectionGenRef.current) return
          // The keybook client failed to load (e.g. a chunk-load error): the
          // same as a failed read — never a spinner forever.
          setVersionReadFailed(true)
          setUpdateError('Failed to load credential')
        })
      }
      setResolving(false)
      return
    }

    if (force || !file) {
      setIsDraft(true)
      setDraftLabel(label || `New credential`)
      setResolving(false)
      return
    }

    async function tryResolve() {
      try {
        const { filesApi } = await import('@papan/files/client')
        const { keybookApi, getKeybookContext } = await import('@papan/keybook/client')
        const { tenantId } = getKeybookContext()
        if (!tenantId) { setIsDraft(true); setDraftLabel(label || `New credential`); setResolving(false); return }

        const relativePath = file!.replace(/^\/papan\/?/, '')
        const pathResult = await filesApi.resolvePath(relativePath).catch(() => null)
        const fileId = pathResult?.id
        resolvedFileId.current = fileId || null

        // Resolution ladder, broadest to narrowest. We want to
        // pre-select an existing entry whenever possible — falling
        // through to "new credential" should only happen when the
        // caller really has nothing to reuse.
        //
        //   1. file binding on the exact file (most specific match)
        //   2. file binding on the parent folder (covers fresh git
        //      clones where the dest folder doesn't exist yet)
        //   3. provider-only match (e.g. github cred shared with
        //      this user) — last because it's the loosest, but
        //      essential: without it, recipients of a shared github
        //      cred kept being prompted to create a new entry even
        //      though they had read access to a working one.
        let resolved: Awaited<ReturnType<typeof keybookApi.resolveByFile>> = { found: false }
        if (fileId) {
          resolved = await keybookApi.resolveByFile(tenantId, { fileId })
          if (!resolved.found) {
            const parentPath = relativePath.split('/').slice(0, -1).join('/')
            if (parentPath) {
              const parentResult = await filesApi.resolvePath(parentPath).catch(() => null)
              if (parentResult?.id) {
                resolved = await keybookApi.resolveByFile(tenantId, { fileId: parentResult.id })
              }
            }
          }
        }

        if (!resolved.found && provider) {
          // resolve-by-provider is owner-OR-shared aware (see
          // entries.ts) so this catches creds the caller doesn't
          // own but has access to. Pass contextRepo so the server
          // scores by repo coverage — picks the cred whose install
          // actually covers the target repo, instead of any-old-
          // github-cred-the-caller-can-see (which would mis-select
          // when the caller has multiple github creds for different
          // orgs).
          resolved = await keybookApi.resolveByProvider(tenantId, { provider, contextRepo })
        }

        if (!resolved.found || !resolved.entry) { setIsDraft(true); setDraftLabel(label || `New credential`); setResolving(false); return }

        // Server returns plaintext under SSE — no client-side decrypt.
        const detail = await keybookApi.getEntry(resolved.entry.id)
        const data = (detail.entry.data ?? { fields: {} }) as { fields: Record<string, string>; fieldsMeta?: Record<string, { sensitive?: boolean }> }

        const resolvedEntry: ResolvedEntry = {
          fields: data.fields,
          entryId: detail.entry.id,
          label: detail.entry.sourceLabel,
          credentialType: detail.entry.credentialType,
          storedData: data as ResolvedEntry['storedData'],
          fieldsMeta: data.fieldsMeta,
          version: versionOf(detail.entry),
        }
        setVersionReadFailed(resolvedEntry.version === undefined)
        if (resolvedEntry.version === undefined) setUpdateError('Failed to load credential')

        // Auto-submit if already approved in this session — only when matching by kind adds nothing over exact names
        // (#1324 owner review, S2): the answer resolves by kind, so a fill the person never saw (a password standing
        // in for an `api_token` the earlier binding didn't ask for) needs the card shown first.
        const kindAddsNothing = isBearerSplatType(detail.entry.credentialType)
          || Object.entries(matchCredentialFields(requestedNames, entryStoredFields(detail.entry.credentialType, presentFieldNames(data.fields), data.fieldsMeta) ?? [], envMapping))
            .every(([requested, stored]) => requested === stored)
        // #1448: never for a request with a `prompt` field — its one-time code is typed on the card each time
        if (alreadyInSession && kindAddsNothing && promptNames.size === 0) {
          // auto-resolved: nothing saved now; #1441 the exact map of these (exact-name) fills
          const autoMap = cardFieldMap(requestedNames, detail.entry.credentialType, data.fields, formValuesForEntry(requestedNames, detail.entry.credentialType, data.fields, data.fieldsMeta, envMapping), data.fieldsMeta, envMapping)
          if (onCredentialResolved) { onCredentialResolved(resolvedEntry.entryId, false, autoMap ? { fieldMap: autoMap } : {}) }
          else { onReply([['Submitted']]) }
          return
        }

        // Map the entry's stored fields onto the agent-declared field
        // names so the Approve button's "any field has a value" check
        // sees something — the shared matcher (#1324), the same rule
        // resolveByEntryId applies on the worker side: a github / PAT
        // bearer fills every declared field; any other entry fills each
        // declared field from the stored field it matches (exact name,
        // else by kind — `api_key` takes an API-key entry's `key`).
        const formValues = blankPrompts(formValuesForEntry(requestedNames, detail.entry.credentialType, data.fields, data.fieldsMeta, envMapping))

        setActiveEntry(resolvedEntry)
        setValues(formValues)
        setOriginalValues(formValues)
        setResolving(false)
      } catch { setResolving(false) }
    }
    tryResolve()
  }, [file, force, alreadyInSession, onReply, onCredentialResolved, provider, label, contextRepo])

  // Fetch all keybook entries for the key rack. Runs ONCE after resolve —
  // relevance scoring includes the (initially) active entry, and re-running
  // it on every switch re-sorted the rack under the user's pointer (the
  // just-picked key jumped to the front). Order is frozen for the prompt's
  // lifetime; selection state is conveyed by the fob highlight, not order.
  const entriesLoadedRef = useRef(false)
  useEffect(() => {
    if (resolving || entriesLoadedRef.current) return
    entriesLoadedRef.current = true
    ;(async () => {
      try {
        const { keybookApi, getKeybookContext } = await import('@papan/keybook/client')
        const { tenantId } = getKeybookContext()
        if (!tenantId) return
        const { entries } = await keybookApi.listEntries(tenantId)
        const fileId = resolvedFileId.current
        // Determine field names we need (for compatibility check)

        const scored = entries.map(e => {
          // Relevance: exact file > same parent > same type > rest.
          // An entry now lists all files it's bound to, so "exact"
          // means the requested fileId appears anywhere in that list.
          let relevance = 0
          const boundFiles = e.fileIds ?? []
          if (fileId && boundFiles.includes(fileId)) relevance = 100
          else if (activeEntry?.entryId === e.id) relevance = 100
          // Provider match (e.g. agent asked for github → existing
          // github / pat entries are compatible). Without this branch
          // saved github creds get marked incompatible because their
          // credentialType isn't in the password/api_key list below.
          const providerMatches = !!provider && e.provider === provider
          // Does the entry's shape supply the request? The shared matcher (#1324): the
          // same rule fills the form and the server's env, so a key offered here resolves.
          const names = requestedNames
          const optional = promptFieldNames(fieldDefs)
          // A `password` entry may hold ANY names — the card's own "Save & use key" stores a request's field
          // names under it — so a listed (undecrypted) one is offered for any request needing a secret, as before
          // #1324; picking it checks its real fields (switchToEntry) before anything fills.
          const fits = entryFitsRequest(names, entryStoredFields(e.credentialType), optional, envMapping)
            || (e.credentialType === 'password' && names.some((n) => !optional.has(n) && isRequiredSecretFor(n)))
          // Restrict to provider-matches ONLY for external OAuth/git
          // providers (github/gitlab/bitbucket), whose bearer is stored
          // under access_token/pat — a generic password can't supply it
          // even though the declared field is named 'token'. cred_to_env's
          // `provider: 'file'` marker (and provider-less requests) are plain
          // field creds, so they use the password heuristic — otherwise every
          // saved password gets wrongly dimmed in a file-bound cred prompt.
          const isExternalProvider = provider === 'github' || provider === 'gitlab' || provider === 'bitbucket'
          const compatible = isExternalProvider ? providerMatches : fits
          if (compatible) relevance += 10
          if (providerMatches) relevance += 20

          return {
            id: e.id,
            // Custom label wins — the user named this key on purpose.
            label: e.customLabel || e.sourceLabel,
            // Owning email (ownerType='email') → avatar initials on the key.
            // A deleted account's key ('deleted:<email>') shows the person's initials.
            owner: e.ownerType === 'email' ? (erasedOwnerEmail(e.ownerId) ?? e.ownerId) : undefined,
            provider: e.provider,
            fileId: boundFiles[0] ?? null,
            credentialType: e.credentialType,
            relevance,
            compatible,
          }
        })

        scored.sort((a, b) => b.relevance - a.relevance)
        setAllEntries(scored)
      } catch {}
    })()
  }, [resolving, fieldDefs, activeEntry, provider])

  // Switch to a different credential entry
  const switchToEntry = useCallback(async (entryId: string, compatible: boolean): Promise<boolean> => {
    // An update is saving: the selection holds until it settles (a switch now
    // would leave its result with no key to land on).
    if (updatingRef.current) return false
    if (!compatible) {
      setSwitchError('This credential format is not compatible with what\'s needed')
      setTimeout(() => setSwitchError(null), 3000)
      return false
    }
    const gen = ++switchGenRef.current
    setSwitchPending(true)
    try {
      const { keybookApi } = await import('@papan/keybook/client')
      const detail = await keybookApi.getEntry(entryId)
      // A later switch (or Reload) has started since: this answer is stale.
      if (gen !== switchGenRef.current) return false
      const data = (detail.entry.data ?? { fields: {} }) as { fields: Record<string, string>; fieldsMeta?: Record<string, { sensitive?: boolean }> }

      // Fill the agent-declared field names from the picked entry — the shared matcher
      // (#1324): a GitHub / PAT bearer fills every field; any other entry fills each field
      // from the stored field it matches (an API key's `key` fills a field named `api_key`).
      const ct = (detail.entry as any).credentialType
      // The listing judged it on its type's schema; its real fields decide (a card-saved `password` entry holds any).
      // Only for PICKING a key: re-reading the one already selected (Reload) is never refused — the card holds it.
      if (!isBearerSplatType(ct) && entryId !== activeEntry?.entryId) {
        if (!entryFitsRequest(requestedNames, entryStoredFields(ct, presentFieldNames(data.fields), data.fieldsMeta), promptFieldNames(fieldDefs), envMapping)) {
          setSwitchError('This credential format is not compatible with what\'s needed')
          setTimeout(() => setSwitchError(null), 3000)
          return false
        }
      }
      const resolvedFields = blankPrompts(formValuesForEntry(requestedNames, ct, data.fields, data.fieldsMeta, envMapping))

      // Save current values as draft before switching (read from DOM to avoid stale closure)
      setValues(prev => {
        if (!activeEntry) draftValuesRef.current = { ...prev }
        return resolvedFields
      })
      const switchedVersion = versionOf(detail.entry)
      selectionGenRef.current++
      setActiveEntry({ fields: resolvedFields, entryId, label: detail.entry.sourceLabel, credentialType: ct, storedData: data as ResolvedEntry['storedData'], fieldsMeta: data.fieldsMeta, version: switchedVersion })
      // A new key starts clean: no earlier key's error or Reload carries over.
      setVersionReadFailed(switchedVersion === undefined)
      setUpdateError(switchedVersion === undefined ? 'Failed to load credential' : null)
      setUpdateChanged(false)
      setSaveError(null)
      setOriginalValues(resolvedFields)
      setSwitchError(null)
      return true
    } catch {
      if (gen !== switchGenRef.current) return false
      setSwitchError('Failed to load credential')
      setTimeout(() => setSwitchError(null), 3000)
      return false
    } finally {
      if (gen === switchGenRef.current) setSwitchPending(false)
    }
  }, [activeEntry, fieldDefs])

  // ─── Speech-bubble X positioning ─────────────────────────────────
  // The bubble hangs above the ACTIVE fob (Y is pure CSS). Its X is
  // measured: centered between bubble and key, but CLAMPED so the
  // bubble never leaves the rack's visible bounds; the tail keeps
  // pointing at the key's center independently of the clamp.
  const rackRef = useRef<HTMLDivElement>(null)
  const activeItemRef = useRef<HTMLDivElement | null>(null)
  const bubbleRef = useRef<HTMLDivElement | null>(null)

  const updateBubble = useCallback(() => {
    const rack = rackRef.current
    const item = activeItemRef.current
    const bubble = bubbleRef.current
    if (!rack || !item || !bubble) return
    const bw = bubble.offsetWidth
    const keyCenter = item.offsetWidth / 2 // wrapper-local
    // Center on the key, capped at the rack CONTENT's leftmost/rightmost —
    // the bubble scrolls with the rack like any content (no follow-the-
    // viewport behavior), it just never hangs past the ends.
    // NOTE: rect-based item position — `item.offsetLeft` is relative to
    // the nearest POSITIONED ancestor (the wrapper itself is one; the
    // rack is static), which put the caps in the wrong coordinate space.
    const itemContentLeft = item.getBoundingClientRect().left
      - rack.getBoundingClientRect().left + rack.scrollLeft
    const desired = keyCenter - bw / 2
    const min = -itemContentLeft
    const max = rack.scrollWidth - bw - itemContentLeft
    const left = Math.max(min, Math.min(desired, Math.max(min, max)))
    const arrowX = Math.max(12, Math.min(keyCenter - left, bw - 12))
    bubble.style.left = `${left}px`
    bubble.style.setProperty('--kb-arrow-x', `${arrowX}px`)
  }, [])

  // Re-measure after every render that can move/resize the bubble or the
  // active fob (selection, rename editing, error line, entries loading).
  useLayoutEffect(updateBubble)

  useEffect(() => {
    window.addEventListener('resize', updateBubble)
    return () => window.removeEventListener('resize', updateBubble)
  }, [updateBubble])

  /** The selected key was chosen again while a switch away from it is still
   *  loading: it is the latest choice, so that switch must not land. */
  const cancelPendingSwitch = useCallback(() => {
    switchGenRef.current++
    setSwitchPending(false)
  }, [])

  /** The "cut a new key" slot in the rack — start (or return to) the
   *  draft. Restores whatever the user had typed before switching to a
   *  saved key (switchToEntry stashes it in draftValuesRef). */
  const handleNewKey = useCallback(() => {
    if (!activeEntry && isDraft) {
      // Already on the draft, but a switch away from it may still be loading:
      // New key was chosen last, so that switch must not land.
      cancelPendingSwitch()
      return
    }
    // An update is saving: the selection holds until it settles.
    if (updatingRef.current) return
    // Leaving the selected key is a selection change: any read or update still
    // settling belongs to the key left.
    selectionGenRef.current++
    // …and a switch still loading must not land after it (New key was chosen last).
    switchGenRef.current++
    setSwitchPending(false)
    setSaveError(null)
    setActiveEntry(null)
    if (!isDraft) {
      setIsDraft(true)
      setDraftLabel(label || 'New credential')
    }
    setValues({ ...draftValuesRef.current })
    setOriginalValues({})
  }, [activeEntry, isDraft, label, cancelPendingSwitch])

  const startRename = useCallback(() => {
    setRenameValue(activeEntry ? (activeEntry.label || '') : draftLabel)
    setRenaming(true)
  }, [activeEntry, draftLabel])

  const commitRename = useCallback(() => {
    const v = renameValue.trim()
    if (v) {
      if (activeEntry?.entryId) {
        const entryId = activeEntry.entryId
        import('@papan/keybook/client').then(({ keybookApi }) => {
          keybookApi.updateEntryMetadata(entryId, { sourceLabel: v }).catch(() => {})
        })
        setActiveEntry(prev => prev ? { ...prev, label: v } : null)
        setAllEntries(prev => prev.map(e => e.id === entryId ? { ...e, label: v } : e))
      } else {
        setDraftLabel(v)
      }
    }
    setRenaming(false)
  }, [renameValue, activeEntry])

  /** The edit card's own read of its key (which applies the shared matcher's fills) hasn't landed — or failed. Save
   *  binds by kind (#1324 owner review, S2), so it waits for the fills the person will be binding, as Update waits
   *  for the version. */
  const editReadPending = !!initialEntryId && activeEntry?.entryId === initialEntryId && activeEntry.storedData === undefined

  const handleApproveOrSubmit = useCallback(async () => {
    // A key switch is still loading: what is on screen is about to change.
    if (switchPending) return
    // #1324 S2: an edit card binds by kind on Save — never before its own read has shown the fills it will bind
    if (editReadPending) return
    // An edited key offers only Update key / Save as new key: binding it as read would leave the edited field unmapped
    // while the model is told its variable is ready (#1441). Enter follows the buttons, so it does nothing here.
    if (isModified) return
    const hasValues = fieldDefs.some(f => ownStr(values, f.name).trim())
    if (!hasValues) return
    setSaving(true)
    setSaveError(null)

    // ALWAYS save to keybook first — the server-resolved per-turn
    // pipeline looks values up by entryId, so without one we'd leave
    // the AI without env vars on the next turn. The "no file" case
    // (cred_to_env without a file binding, e.g. a generic API key)
    // saves with a synthesized label and no fileId binding.
    let entryId = activeEntry?.entryId
    let saved = false // an existing entry approved as-is is not a save
    // #1441: the key as it stands once this submit is done — the entry as read, or the new key it writes
    const answer = answerFor(activeEntry?.credentialType ?? (activeEntry ? undefined : 'password'), activeEntry ? activeEntry.storedData?.fields as Record<string, string> | undefined : blankPrompts(values), activeEntry?.fieldsMeta)
    // #1448: a request whose only typed values are one-time codes has nothing to keep — no key is saved (an empty one
    // would pile up per answer); the answer carries the codes alone
    if (!entryId && !fieldDefs.some((f) => !promptNames.has(f.name) && ownStr(values, f.name).trim())) {
      if (onCredentialResolved) onCredentialResolved(undefined, false, answer)
      else onReply([['Submitted']])
      return
    }
    if (!entryId) {
      const fallbackLabel = isDraft ? draftLabel : label
      // #1448: a one-time code is never written to the key
      const newId = await saveToKeybook(blankPrompts(values), file, fallbackLabel)
      if (!newId) {
        // Nothing was saved: never answer with no key (the turn would run without
        // it) and never leave the buttons disabled — say so, and let them retry.
        setSaving(false)
        setSaveError(SAVE_FAILED)
        return
      }
      entryId = newId; saved = true
    }

    if (onCredentialResolved) {
      onCredentialResolved(entryId, saved, answer)
    } else {
      onReply([['Submitted']])
    }
  }, [fieldDefs, values, onReply, onCredentialResolved, activeEntry, file, label, isDraft, draftLabel, switchPending, editReadPending, isModified])

  const handleUpdate = useCallback(async () => {
    if (!activeEntry?.entryId) return
    // Only ever against the version read: no version yet, no write (never an unconditional overwrite).
    if (activeEntry.version === undefined) return
    const expectedVersion = activeEntry.version
    setSaving(true)
    setUpdateError(null)
    setUpdateChanged(false)

    // I198 / D56: update the entry FIRST and reply only once it landed. It used to
    // reply first and update in the background with the failure swallowed — so the
    // turn ran on the entry's OLD value, and a quick expiry would claim "your key was
    // saved" for a write that failed or hadn't happened yet.
    // Sent against the version the prompt read: a key changed meanwhile is
    // refused, and the person's draft stays in the form.
    const updatedId = activeEntry.entryId
    const genAtStart = selectionGenRef.current
    updatingRef.current = true
    let outcome: Awaited<ReturnType<typeof updateKeybookEntry>>
    let stored: Record<string, string> = {}
    try {
      // #1324: back onto the entry's own field names (a value typed into `api_key` is the API key's `key`)
      // Started from the entry's stored fields (the PATCH replaces the whole payload): a field the form doesn't show
      // — an edit card asking for `password` alone over a `{username, password}` entry — is kept, never dropped.
      const base = (activeEntry.storedData?.fields ?? {}) as Record<string, string>   // the entry as READ — never the form's fills
      // #1448: a one-time code is never written to the key
      stored = storedValuesFromForm(requestedNames, activeEntry.credentialType ?? 'password', base, blankPrompts(values), activeEntry.fieldsMeta, envMapping)
      outcome = await updateKeybookEntry(updatedId, {
        ...(activeEntry.storedData ?? {}),
        fields: stored,
        // the entry's own type and per-field sensitivity: an API key updated from the card stays an API key, a field
        // marked secret stays one (matching reads `fieldsMeta`)
        credentialType: activeEntry.credentialType ?? 'password',
        ...(activeEntry.fieldsMeta ? { fieldsMeta: activeEntry.fieldsMeta } : {}),
      }, expectedVersion)
    } finally {
      updatingRef.current = false
    }
    // A switch began while this saved (even one still loading, or away and
    // back): its result belongs to the selection they left — never shown
    // beside, or submitted as, what is selected now.
    if (selectionGenRef.current !== genAtStart) { setSaving(false); return }
    if (outcome !== 'ok') {
      setSaving(false)
      setUpdateChanged(outcome === 'changed')
      setUpdateError(outcome === 'changed'
        ? t('entryChanged.message')
        : 'Failed to update the saved key — try again')
      return
    }

    // Accepted (#1324 owner review, owner 2026-10-27; follow-up #1441): the write-back changes the key's fields, so
    // kind matching may now fill a requested field the form showed EMPTY — two secrets the card couldn't tell apart,
    // one typed: the other can take the key's old secret. The value stays in this person's own session (wrong
    // variable, never anyone else's). The fix — the binding carries the exact field map the card showed — is the
    // follow-up's.
    // #1441 (owner, 2026-10-28): the answer carries the exact map of what this card showed, judged on the key as just
    // written — so a requested field it showed EMPTY (two secrets it couldn't tell apart, one typed) never takes the
    // key's other secret, on this turn or any later one.
    if (onCredentialResolved) {
      onCredentialResolved(activeEntry.entryId, true, answerFor(activeEntry.credentialType ?? 'password', stored, activeEntry.fieldsMeta))
    } else {
      onReply([['Submitted']])
    }
  }, [values, activeEntry, onReply, onCredentialResolved, t, fieldDefs])

  const handleSaveAs = useCallback(async () => {
    if (switchPending) return // as Submit: the values are about to change
    setSaving(true)
    setSaveError(null)
    // Save as new entry first so we can resolve with its real id. #1324: a copy of a picked key is written back like
    // Update — its own fields with the edits, never a stored name beside its alias fill (two copies of a secret, one
    // stale); a GitHub / PAT key, or no picked key, saves the form as it is (as before #1324).
    const ct = activeEntry?.credentialType
    const loaded = activeEntry?.storedData?.fields as Record<string, string> | undefined
    // #1448: a one-time code is never written to the key
    let toStore: Record<string, string> = blankPrompts(values)
    let shape: { credentialType?: string; fieldsMeta?: Record<string, { sensitive?: boolean }> } | undefined
    if (loaded && ct && !isBearerSplatType(ct)) {
      // a copy of the picked key with the edits — written back like Update (its other fields kept, no alias copy),
      // under its own type when the keybook still accepts it (a pre-#1346 unknown type → a password key, as before)
      toStore = storedValuesFromForm(requestedNames, ct, loaded, blankPrompts(values), activeEntry?.fieldsMeta, envMapping)
      shape = { credentialType: isKnownCredentialType(ct) ? ct : 'password', fieldsMeta: activeEntry?.fieldsMeta }
    }
    const entryId = await saveToKeybook(toStore, file, label ? `${label} (copy)` : undefined, shape)
    if (!entryId) { setSaving(false); setSaveError(SAVE_FAILED); return } // as Submit
    if (onCredentialResolved) {
      onCredentialResolved(entryId, true, answerFor(shape?.credentialType ?? 'password', toStore, shape?.fieldsMeta))
    } else {
      onReply([['Submitted']])
    }
  }, [values, file, label, onReply, onCredentialResolved, switchPending, fieldDefs, activeEntry])

  const handleLater = useCallback(() => {
    onReply([['Do this later']])
  }, [onReply])

  // ─── GitHub flow helpers ─────────────────────────────────────────

  /** Open the GitHub App install popup. Server creates the entry
   *  directly from the OAuth callback; the BC listener below handles
   *  the post-install bind + submit. */
  const handleConnectGithub = useCallback(async () => {
    try {
      const { getKeybookContext } = await import('@papan/keybook/client')
      const { tenantId } = getKeybookContext()
      if (!tenantId) return
      const apiBase = getApiBase()
      const origin = window.location.origin
      const labelParam = label ? `&label=${encodeURIComponent(label)}` : ''
      const url = `${apiBase}/api/keybook/oauth/github/start?tenantId=${encodeURIComponent(tenantId)}&origin=${encodeURIComponent(origin)}${labelParam}`
      window.open(url, 'papan-github-install', 'width=1020,height=940')
      setAuthorizing(true)
    } catch (err) {
      console.warn('[cred-prompt] failed to open github oauth popup:', err)
    }
  }, [label])

  /** Validate the pasted PAT against GitHub. Mirrors the verify flow
   *  in EntryCreateDialog so the user gets the same "✓ verified, X
   *  repos" summary before save. */
  const handleValidatePat = useCallback(async () => {
    const token = patValue.trim()
    if (!token) { setPatError('Paste a token first'); return }
    setPatValidating(true)
    setPatError(null)
    try {
      const { keybookApi } = await import('@papan/keybook/client')
      const result = await keybookApi.validatePat({ provider: 'github', token })
      if (!result.ok) {
        setPatError(result.error || 'Token validation failed')
        setPatValidation(null)
      } else {
        setPatValidation({
          accountLogin: result.accountLogin!,
          accountType: result.accountType!,
          repositoryNames: result.repositoryNames!,
          repositoryCount: result.repositoryCount!,
          truncated: !!result.truncated,
          repositorySelection: result.repositorySelection,
          scope: result.scope,
        })
      }
    } catch (err: any) {
      const text = errorText(err, 'Token validation failed')   // #1316: a dropped answer shows nothing
      if (text !== null) setPatError(text)
      setPatValidation(null)
    } finally {
      setPatValidating(false)
    }
  }, [patValue])

  /** PAT path — save a new keybook entry, then bind it as an active
   *  session credential by mapping the PAT under each declared field
   *  name (the agent typically asks for 'token'). */
  const handleSubmitPat = useCallback(async () => {
    if (switchPending) return // as Submit: a saved key is about to be selected
    if (!patValidation) {
      setPatError('Validate the token first (click "Verify token")')
      return
    }
    setSaving(true)
    try {
      const { filesApi } = await import('@papan/files/client')
      const { keybookApi, getKeybookContext } = await import('@papan/keybook/client')
      const { tenantId, orgEmail } = getKeybookContext()
      if (!tenantId || !orgEmail) throw new Error('No active tenant/email')

      const fileId = file
        ? (await filesApi.resolvePath(file.replace(/^\/papan\/?/, '')).catch(() => null))?.id
        : undefined

      const sourceLabel = label || `GitHub PAT: ${patValidation.accountLogin}`
      const created = await keybookApi.createEntry(tenantId, {
        ownerType: 'email',
        ownerId: orgEmail.toLowerCase(),
        sourceLabel,
        credentialType: 'pat',
        provider: 'github',
        fileId,
        data: {
          credentialType: 'pat',
          fields: {
            pat: patValue,
            account_login: patValidation.accountLogin,
            account_type: patValidation.accountType,
            repository_count: String(patValidation.repositoryCount),
            repository_names: JSON.stringify(patValidation.repositoryNames),
            repository_truncated: patValidation.truncated ? '1' : '0',
            ...(patValidation.repositorySelection && { repository_selection: patValidation.repositorySelection }),
            ...(patValidation.scope && { scope: patValidation.scope }),
          },
        } as any,
      })

      // The keybook entry is now persisted; the per-turn resolver
      // will splat the PAT across the cred's envMapping at job
      // submit time. No values to pass up.
      if (onCredentialResolved) onCredentialResolved(created.entryId, true) // the PAT entry was just created
      else onReply([['Submitted']])
    } catch (err: any) {
      setSaving(false)
      const text = errorText(err, 'Failed to save')   // #1316: a dropped answer shows nothing
      if (text !== null) setPatError(text)
    }
  }, [patValue, patValidation, file, label, onCredentialResolved, onReply, switchPending])

  // A saved key is selected (or loading): a GitHub authorize finishing after that
  // must not answer over it. Read by the OAuth listener, which outlives renders.
  const savedKeyChosenRef = useRef(false)
  savedKeyChosenRef.current = !!activeEntry || switchPending

  /** OAuth listener — only active in the 'authorize' branch. When the
   *  install popup completes, the callback emits via BroadcastChannel
   *  (primary, COOP-safe) or postMessage (fallback). We fetch the new
   *  entry, splice the access_token under each declared field name,
   *  and submit. */
  useEffect(() => {
    if (!provider || provider !== 'github') return
    if (githubMode !== 'authorize') return

    let cancelled = false
    const seen = new Set<string>()

    const handleInstalled = async (entryId: string) => {
      if (cancelled) return
      // The person picked a saved key after starting the authorize: theirs is the
      // later choice. The installation's key stays in the keybook, unused here —
      // and isn't marked seen, so authorizing the same account again still answers
      // (the callback re-sends the same entry id).
      if (savedKeyChosenRef.current) { setAuthorizing(false); return }
      if (seen.has(entryId)) return
      seen.add(entryId)
      try {
        // We still fetch to confirm the entry has an access_token —
        // the OAuth callback might have failed mid-flight. After this
        // gate, the per-turn resolver pulls the value at job submit.
        const { keybookApi } = await import('@papan/keybook/client')
        const detail = await keybookApi.getEntry(entryId)
        const data = (detail.entry.data ?? { fields: {} }) as { fields: Record<string, string> }
        if (!data.fields.access_token) {
          seen.delete(entryId) // a later, successful re-authorize re-sends this id
          setAuthorizing(false)
          return
        }
        // …or picked one while this read was in flight. (Not `cancelled`: a parent
        // re-render replaces this listener mid-read, and the install must still land.)
        if (savedKeyChosenRef.current) { seen.delete(entryId); setAuthorizing(false); return }
        setSaving(true)
        // The OAuth install created this entry just now and its token is confirmed.
        if (onCredentialResolved) onCredentialResolved(entryId, true)
        else onReply([['Submitted']])
      } catch (err) {
        console.warn('[cred-prompt] failed to bind installed github entry:', err)
        seen.delete(entryId)
        setAuthorizing(false)
      }
    }

    let ch: BroadcastChannel | null = null
    try {
      ch = new BroadcastChannel('papan-github-install')
      ch.onmessage = (ev) => {
        if (ev.data?.type === 'installed' && ev.data.entryId) handleInstalled(ev.data.entryId)
      }
    } catch { /* unsupported — postMessage will catch it */ }

    function onMessage(ev: MessageEvent) {
      if (ev.data?.type !== 'keybook-github-installed') return
      if (ev.data.entryId) handleInstalled(ev.data.entryId)
    }
    window.addEventListener('message', onMessage)

    return () => {
      cancelled = true
      ch?.close()
      window.removeEventListener('message', onMessage)
    }
  }, [provider, githubMode, onCredentialResolved, onReply])

  if (resolving) {
    return (
      <div className="kb-card p-4 flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" style={{ color: 'var(--btn-primary-bg)' }} />
        <span className="text-sm themed-text-muted">Checking your Keybook…</span>
      </div>
    )
  }

  const fileName = file?.split('/').pop()

  return (
    <div className="kb-card" data-testid="credential-card">
      {/* Header — section title + gold Keybook badge + what the key unlocks */}
      <div className="px-4 pt-3">
        <div
          className="text-[10px] uppercase tracking-wider font-medium mb-2"
          style={{ color: 'var(--kb-gold)' }}
        >
          Credential required
        </div>
        <div className="flex items-center gap-2.5">
          <span
            className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
            style={{ background: 'color-mix(in srgb, var(--kb-gold) 16%, transparent)' }}
          >
            <BookKey size={15} style={{ color: 'var(--kb-gold)' }} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium themed-text truncate">
              {label || 'Credentials needed'}
            </div>
            {fileName && (
              <div className="text-[11px] themed-text-muted truncate">unlocks {fileName}</div>
            )}
          </div>
          {isEdit && activeEntry && onDelete && (
            <button
              onClick={() => setConfirmingDelete(true)}
              className="text-red-400 hover:text-red-300 transition-colors"
              title="Remove from session"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation */}
      {confirmingDelete && (
        <div className="px-4 py-3 border-b border-red-500/30 bg-red-500/10">
          <p className="text-xs themed-text mb-2">Remove this credential from the session? The AI will <span className="text-red-400 font-semibold">no longer have access</span> to these environment variables.</p>
          <div className="flex gap-2">
            <button
              onClick={() => { onDelete?.(); setConfirmingDelete(false) }}
              className="px-2.5 py-1 rounded text-xs bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              Remove
            </button>
            <button
              onClick={() => setConfirmingDelete(false)}
              className="px-2.5 py-1 rounded text-xs themed-text-muted hover:themed-text transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Key rack — pick a key from the Keybook FIRST, form follows.
          The rack replaces both the old "Using:/New:" status line and the
          scattered tag row: every key is a labeled fob with its owner.
          Shown in the GitHub flow too — picking a saved GitHub key sets
          activeEntry (which flips isGithubFlow off → read-only summary);
          the active "New key" fob is the authorize/PAT chooser below. */}
      {(allEntries.length > 0 || isDraft || activeEntry) && (() => {
        {/* Speech bubble hovering over the SELECTED fob (tail points down
            at it). Rendered inside the active key's scroll-item wrapper so
            it scrolls with the rack; the scroller's top padding gives it
            headroom. */}
        const rackBubble = (
          <div className="kb-rack-bubble" ref={bubbleRef}>
            {switchError ? (
              <span className="text-[10px] text-red-400">{switchError}</span>
            ) : renaming ? (
              <span className="flex items-center gap-1">
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') commitRename()
                    if (e.key === 'Escape') setRenaming(false)
                  }}
                  className="themed-input px-1.5 py-0.5 rounded text-[11px] w-32"
                  autoFocus
                />
                <button onClick={commitRename} className="themed-text-muted hover:themed-text shrink-0" title="Save name">
                  <Check size={12} />
                </button>
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <span className="text-[11px] themed-text max-w-[11rem] truncate">
                  {activeEntry ? (activeEntry.label || 'Saved key') : draftLabel}
                </span>
                <button onClick={startRename} className="themed-text-muted hover:themed-text shrink-0" title="Rename this key">
                  <Pencil size={10} />
                </button>
              </span>
            )}
          </div>
        )
        return (
          <div className="kb-rack-strip">
            <div ref={rackRef} className="flex gap-2 overflow-x-auto pt-10 pb-2.5 px-4">
              <div className="relative shrink-0" ref={el => { if (!activeEntry) activeItemRef.current = el }}>
                {!activeEntry && rackBubble}
                <KeybookKeyCard
                  isNew
                  label={isDraft && !activeEntry ? draftLabel : 'New key'}
                  active={!activeEntry}
                  onClick={handleNewKey}
                  title="Enter a new credential"
                />
              </div>
              {/* Show the active key even if it's past the first 5 (so it's
                  never hidden), then fill the rest up to 5 total. */}
              {(() => {
                const top = allEntries.slice(0, 5)
                if (activeEntry && !top.some(e => e.id === activeEntry.entryId)) {
                  const act = allEntries.find(e => e.id === activeEntry.entryId)
                  if (act) top[top.length - 1] = act
                }
                return top
              })().map(entry => (
                <div
                  key={entry.id}
                  className="relative shrink-0"
                  ref={el => { if (entry.id === activeEntry?.entryId) activeItemRef.current = el }}
                >
                  {entry.id === activeEntry?.entryId && rackBubble}
                  <KeybookKeyCard
                    label={entry.label}
                    owner={entry.owner}
                    provider={entry.provider}
                    credentialType={entry.credentialType}
                    active={entry.id === activeEntry?.entryId}
                    disabled={!entry.compatible}
                    onClick={() => entry.id === activeEntry?.entryId ? cancelPendingSwitch() : switchToEntry(entry.id, entry.compatible)}
                    title={!entry.compatible ? `${entry.label} — format not compatible with this request` : entry.label}
                  />
                </div>
              ))}
              <button
                onClick={() => setShowAllEntries(true)}
                className="kb-open-keybook shrink-0 self-stretch w-30 flex flex-col items-center justify-center gap-1 rounded-[0.625rem] border themed-border themed-text-muted hover:themed-text transition-colors"
                title="Browse all keys in your Keybook"
              >
                <BookKey size={15} />
                <span className="text-[9px] uppercase tracking-wider whitespace-nowrap">Open Keybook</span>
              </button>
            </div>
            {activeEntry?.fromSibling && (
              <div className="px-4 pt-2 text-xs themed-text-secondary">
                Found credentials from <strong>{activeEntry.siblingFileName}</strong> in the same folder
              </div>
            )}
          </div>
        )
      })()}

      {/* Form body — generic field list OR GitHub picker, depending
          on provider. The GitHub branch renders its own buttons
          (Authorize / Verify) so the bottom action row hides its
          Submit when we're in this flow. */}
      {isGithubFlow ? (
        <div className="p-4 space-y-3">
          {/* Mode picker — initial screen, two equal-weight options
              with honest trade-off blurbs. Same copy as
              EntryCreateDialog so users see one consistent story. */}
          {githubMode === 'picker' && (
            <div className="space-y-2">
              <p className="text-xs themed-text-muted">
                How should {productName} access GitHub?
              </p>
              <button
                type="button"
                onClick={() => setGithubMode('authorize')}
                data-testid="github-pick-authorize"
                className="kb-panel kb-panel-btn w-full text-left rounded-md transition-colors p-3 space-y-1"
              >
                <div className="flex items-center gap-2 themed-text text-sm font-medium">
                  <GithubIcon size={14} />
                  Authorize on GitHub
                </div>
                <p className="text-xs themed-text-muted leading-relaxed">
                  One click. Acts as you (commits, pushes show as your account). Not
                  recommended to share with others — your activity gets attributed to them.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setGithubMode('pat')}
                data-testid="github-pick-pat"
                className="kb-panel kb-panel-btn w-full text-left rounded-md transition-colors p-3 space-y-1"
              >
                <div className="flex items-center gap-2 themed-text text-sm font-medium">
                  <Key size={14} />
                  Personal Access Token
                </div>
                <p className="text-xs themed-text-muted leading-relaxed">
                  Fine-grained control. Pick exact repos and read/write per permission.
                  Best when you plan to share with teammates who shouldn't have their own
                  GitHub access.
                </p>
              </button>
            </div>
          )}

          {/* Authorize branch — opens the install popup, then the
              BC listener (above) auto-binds the new entry's
              access_token and submits. */}
          {githubMode === 'authorize' && (
            <div className="kb-panel rounded-md p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 themed-text text-sm font-medium">
                  <GithubIcon size={14} />
                  Authorize a GitHub account or organization
                </div>
                <button
                  type="button"
                  onClick={() => { setGithubMode('picker'); setAuthorizing(false) }}
                  className="text-xs themed-text-muted hover:themed-text"
                >
                  Back
                </button>
              </div>
              <p className="text-xs themed-text-muted leading-relaxed">
                Authorizes {productName} to access the repositories you choose. Each
                authorized account gets its own keybook entry — authorize once
                per account or organization.
              </p>
              {authorizing ? (
                <div className="flex items-center gap-2 text-xs themed-text-muted">
                  <Loader2 size={14} className="animate-spin" />
                  Waiting for GitHub authorization…
                  <button
                    type="button"
                    onClick={handleConnectGithub}
                    className="ml-auto themed-text-secondary hover:themed-text underline underline-offset-2"
                  >
                    Reopen popup
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectGithub}
                  data-testid="github-authorize-button"
                  className="w-full themed-btn-primary px-3 py-2 rounded-md text-sm flex items-center justify-center gap-2"
                >
                  <GithubIcon size={14} />
                  Authorize on GitHub
                  <ExternalLink size={12} />
                </button>
              )}
            </div>
          )}

          {/* PAT branch — input + verify + summary. Submit happens
              through handleSubmitPat, wired into the bottom action
              row when patValidation is set. */}
          {githubMode === 'pat' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 themed-text text-sm font-medium">
                  <Key size={14} />
                  Personal Access Token
                </div>
                <button
                  type="button"
                  onClick={() => { setGithubMode('picker'); setPatValidation(null); setPatError(null); setPatValue('') }}
                  className="text-xs themed-text-muted hover:themed-text"
                >
                  Back
                </button>
              </div>
              <a
                href={`https://github.com/settings/personal-access-tokens/new?name=${encodeURIComponent(productName)}&description=${encodeURIComponent(`Read-only repo access for ${productName} agent`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs themed-text-secondary hover:themed-text underline underline-offset-2"
              >
                Open GitHub PAT page
                <ExternalLink size={11} />
              </a>
              <p className="text-xs themed-text-muted leading-relaxed">
                Recommended scope: <strong>Repository access → Only select repositories</strong>,
                <strong> Permissions → Contents: Read</strong> (or Read+Write). Then paste
                below and Verify.
              </p>
              <PasswordInput
                value={patValue}
                onChange={(e) => setPatValue(e.target.value)}
                className="themed-input w-full px-3 py-1.5 rounded text-sm font-mono"
                placeholder="github_pat_…"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleValidatePat}
                  disabled={patValidating || !patValue.trim()}
                  className="themed-btn-secondary px-3 py-1.5 rounded-md text-xs disabled:opacity-30"
                >
                  {patValidating ? 'Verifying…' : patValidation ? 'Re-verify' : 'Verify token'}
                </button>
                {patError && <span className="text-xs text-red-400">{patError}</span>}
              </div>
              {patValidation && (
                <div className="rounded-md themed-bg-tertiary p-3 space-y-1.5 text-xs">
                  <div className="themed-text font-medium">✓ Token verified</div>
                  <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 themed-text-muted">
                    <dt>Owner</dt>
                    <dd className="themed-text">{patValidation.accountLogin} ({patValidation.accountType})</dd>
                    <dt>Repos</dt>
                    <dd className="themed-text">
                      {patValidation.repositoryCount}
                      {patValidation.repositorySelection === 'all' ? ' (all accessible)' : ''}
                      {patValidation.truncated ? ' — list truncated' : ''}
                    </dd>
                    {patValidation.scope && <>
                      <dt>Scope</dt>
                      <dd className="themed-text font-mono text-[11px]">{patValidation.scope}</dd>
                    </>}
                  </dl>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (activeEntry?.credentialType === 'github' || activeEntry?.credentialType === 'pat') ? (
        // Read-only summary for github / pat — the bearer token is
        // either server-managed (github OAuth) or already secret +
        // pre-validated (pat); rendering an editable input would
        // either confuse the user or expose the value in plaintext.
        // The Approve button further down still works because values
        // were spliced under each fieldDef.name at switch time.
        <div className="p-4 space-y-2">
          {(() => {
            const f = activeEntry!.fields ?? {}
            const account = f.account_login || 'unknown'
            const accountType = f.account_type || 'User'
            const repoCount = parseInt(f.repository_count || '0', 10)
            const repoSelection = f.repository_selection
            const repoNames: string[] = (() => {
              try { return JSON.parse(f.repository_names || '[]') } catch { return [] }
            })()
            const repoTruncated = f.repository_truncated === '1'
            const isApp = activeEntry!.credentialType === 'github'
            const scopeLabel = repoSelection === 'all'
              ? `All repositories${repoCount ? ` (${repoCount} accessible)` : ''}`
              : repoSelection === 'selected'
                ? `Selected${repoCount ? ` (${repoCount} repo${repoCount === 1 ? '' : 's'})` : ''}`
                : repoCount > 0 ? `${repoCount} repo${repoCount === 1 ? '' : 's'}` : 'Unknown'
            return (
              <div className="kb-panel rounded-md p-3 space-y-2">
                <div className="flex items-center gap-2 themed-text text-sm font-medium">
                  <GithubIcon size={14} />
                  {isApp ? `Authorized: ${account}` : `Verified PAT: ${account}`}
                  <span className="text-xs themed-text-muted">({accountType})</span>
                </div>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs themed-text-muted">
                  <dt>Account</dt>
                  <dd className="themed-text">{account}</dd>
                  <dt>Scope</dt>
                  <dd className="themed-text">{scopeLabel}</dd>
                  {/* Dedupe by env var value — hidden providers alias
                      every declared field to the same internal env var
                      (e.g. github routes through a credential helper),
                      so listing one row per field would print the same
                      "$__PAPAN_CRED_GITHUB_xxx" twice. Non-hidden
                      providers naturally have distinct values per
                      field so they render unchanged. */}
                  {Array.from(new Set(Object.values(envMapping))).map(envVar => (
                    <Fragment key={envVar}>
                      <dt>Env var</dt>
                      <dd className="themed-text font-mono text-[11px]">${envVar}</dd>
                    </Fragment>
                  ))}
                </dl>
                {repoNames.length > 0 && (
                  <div className="rounded themed-bg-tertiary p-2 max-h-24 overflow-y-auto">
                    <ul className="font-mono text-[11px] themed-text space-y-0.5">
                      {repoNames.slice(0, 6).map(name => (
                        <li key={name} className="truncate" title={name}>{name}</li>
                      ))}
                      {(repoNames.length > 6 || repoTruncated) && (
                        <li className="themed-text-muted">
                          {repoTruncated && repoNames.length <= 6
                            ? '…list truncated'
                            : `…+${repoNames.length - 6} more`}
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      ) : (
        <div className="px-4 pb-4 pt-1 space-y-3">
          {fieldDefs.map(field => (
            <div key={field.name}>
              <label className="flex items-baseline justify-between gap-2 mb-1">
                <span className="text-xs themed-text-secondary capitalize">{field.name.replace(/_/g, ' ')}</span>
                {envNameOf(field.name) && (
                  <code
                    data-testid="credential-env-name"
                    className="text-[10px] font-mono themed-text-muted px-1.5 py-px rounded bg-black/15"
                    title={`The agent reads this as $${envNameOf(field.name)}`}
                  >
                    ${envNameOf(field.name)}
                  </code>
                )}
              </label>
              {/* masked when declared a password, or when its NAME is a secret's (#1324 owner review, S3): a field the
                  shared matcher fills from a key's secret — an edit card's `pwd`, a `prompt` field named `mfa_token` —
                  never shows that secret in clear; nor one filled from a secret stored field, whatever its name. A
                  declared textarea (a certificate) stays a textarea. */}
              {field.type === 'password' || (field.type !== 'textarea' && (classifyFieldName(field.name).kind === 'secret' || secretSourced.has(field.name))) ? (
                <PasswordInput
                  value={ownStr(values, field.name)}
                  onChange={(e) => { setValues(prev => { const next = { ...prev, [field.name]: e.target.value }; if (!activeEntry) draftValuesRef.current = next; return next }); }}
                  className="themed-input w-full px-3 py-1.5 rounded text-sm"
                  placeholder={field.name}
                  onKeyDown={(e) => e.key === 'Enter' && handleApproveOrSubmit()}
                />
              ) : field.type === 'textarea' ? (
                <textarea
                  value={ownStr(values, field.name)}
                  onChange={(e) => { setValues(prev => { const next = { ...prev, [field.name]: e.target.value }; if (!activeEntry) draftValuesRef.current = next; return next }); }}
                  className="themed-input w-full px-3 py-1.5 rounded text-sm font-mono"
                  rows={3}
                  placeholder={field.name}
                />
              ) : (
                <input
                  type="text"
                  value={ownStr(values, field.name)}
                  onChange={(e) => { setValues(prev => { const next = { ...prev, [field.name]: e.target.value }; if (!activeEntry) draftValuesRef.current = next; return next }); }}
                  className="themed-input w-full px-3 py-1.5 rounded text-sm"
                  placeholder={field.name}
                  onKeyDown={(e) => e.key === 'Enter' && handleApproveOrSubmit()}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Credential picker dialog */}
      {showAllEntries && (
        <CredentialPickerDialog
          entries={allEntries}
          activeEntryId={activeEntry?.entryId}
          onSelect={(entryId, compatible) => {
            // As the rack: choosing the selected key again cancels a pending switch
            // (re-reading it would overwrite an unsaved edit).
            if (entryId === activeEntry?.entryId) cancelPendingSwitch()
            else switchToEntry(entryId, compatible)
            if (compatible) setShowAllEntries(false)
          }}
          onClose={() => setShowAllEntries(false)}
        />
      )}

      {/* Sibling folder actions */}
      {activeEntry?.fromSibling && !isModified && (
        <div className="px-4 pb-2 flex gap-2 text-xs">
          <button
            onClick={() => { handleApproveOrSubmit() }}
            disabled={switchPending}
            className="themed-btn-primary px-2.5 py-1 rounded transition-colors disabled:opacity-30"
          >
            Yes, for this file
          </button>
          <button
            onClick={() => {
              onFolderUpgrade?.(activeEntry.entryId)
              handleApproveOrSubmit()
            }}
            disabled={switchPending}
            className="themed-btn-secondary px-2.5 py-1 rounded transition-colors disabled:opacity-30"
          >
            Yes, for all files in folder
          </button>
        </div>
      )}

      {/* Actions — "New" lives in the rack (the dashed key), not here */}
      <div className="flex items-center gap-2 px-4 py-3 border-t themed-border flex-wrap">
        {isGithubFlow ? (
          // GitHub flow: the Authorize button lives inside the panel
          // (auto-submits via BC listener); the PAT branch needs a
          // Save & Submit here once the token is verified.
          githubMode === 'pat' && (
            <button
              onClick={handleSubmitPat}
              disabled={saving || switchPending || !patValidation}
              className="themed-btn-primary flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-30"
            >
              <Check size={14} />
              Save & Submit
            </button>
          )
        ) : !activeEntry?.fromSibling && (
          isModified ? (
            <>
              <button
                onClick={handleUpdate}
                // An existing key's version must have loaded first (saves are version-checked).
                disabled={saving || switchPending || activeEntry?.version === undefined}
                aria-busy={activeEntry?.version === undefined && !versionReadFailed ? true : undefined}
                className="themed-btn-primary flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-30"
              >
                {activeEntry?.version === undefined && !versionReadFailed
                  ? <Loader2 size={14} className="animate-spin" />
                  : <RefreshCw size={14} />}
                Update key
              </button>
              <button
                onClick={handleSaveAs}
                disabled={saving || switchPending}
                className="themed-btn-secondary flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-30"
              >
                <Plus size={14} />
                Save as new key
              </button>
              {updateError && <span role="alert" className="text-xs text-red-400">{updateError}</span>}
              {(updateChanged || versionReadFailed) && activeEntry?.entryId && (
                // As the keybook editor's Reload: re-read the key (the prompt's own
                // read, which takes its new version) and REPLACE the draft with the
                // current value; the message goes once that landed.
                <button
                  type="button"
                  onClick={async () => {
                    // switchToEntry owns the error state: it clears it when the
                    // re-read has a version, and keeps "Failed to load credential"
                    // when it doesn't — so only the stale-save notice is cleared here.
                    if (await switchToEntry(activeEntry.entryId, true)) {
                      setUpdateChanged(false)
                    }
                  }}
                  disabled={saving || switchPending}
                  className="themed-btn-secondary flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-30"
                  data-testid="cred-reload-changed"
                >
                  <RefreshCw size={14} />
                  {t('entryChanged.reload')}
                </button>
              )}
            </>
          ) : (
            <>
              <button
                onClick={handleApproveOrSubmit}
                disabled={saving || switchPending || editReadPending || !fieldDefs.some(f => ownStr(values, f.name).trim())}
                className="themed-btn-primary flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-30"
              >
                <Check size={14} />
                {isEdit ? 'Save' : activeEntry ? 'Use this key' : 'Save & use key'}
              </button>
              {/* the edit card's own read failed: Save waits for it (#1324 S2), so say why and offer the re-read */}
              {editReadPending && versionReadFailed && activeEntry?.entryId && (
                <>
                  {updateError && <span role="alert" className="text-xs text-red-400">{updateError}</span>}
                  <button
                    type="button"
                    onClick={() => { void switchToEntry(activeEntry.entryId, true) }}
                    disabled={saving || switchPending}
                    className="themed-btn-secondary flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors disabled:opacity-30"
                    data-testid="cred-reload-edit-read"
                  >
                    <RefreshCw size={14} />
                    {t('entryChanged.reload')}
                  </button>
                </>
              )}
            </>
          )
        )}
        {saveError && <span role="alert" className="text-xs text-red-400" data-testid="cred-save-error">{saveError}</span>}
        <button
          onClick={handleLater}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm themed-text-muted hover:themed-text transition-colors"
        >
          <Clock size={14} />
          {isEdit ? 'Cancel' : 'Do this later'}
        </button>
      </div>
    </div>
  )
}

// ─── Background keybook operations ──────────────────────────────────

const SAVE_FAILED = 'Failed to save the key — try again'

async function saveToKeybook(
  values: Record<string, string>,
  file: string | undefined,
  label?: string,
  /** Save As of a picked key: its own type and per-field sensitivity (default: a new `password` key) */
  shape?: { credentialType?: string; fieldsMeta?: Record<string, { sensitive?: boolean }> },
): Promise<string | undefined> {
  try {
    const { filesApi } = await import('@papan/files/client')
    const { keybookApi, getKeybookContext } = await import('@papan/keybook/client')

    const { tenantId, orgEmail } = getKeybookContext()
    if (!tenantId || !orgEmail) {
      console.warn('[cred-prompt] No active tenant/email — cannot save to keybook')
      return
    }

    // file is optional — cred_to_env may request a credential not tied
    // to a specific drive file (e.g. generic API keys). Without a file
    // binding the entry is owner-scoped only and shows up in the
    // keybook list at the user's tenant level.
    let fileId: string | undefined
    if (file) {
      const relativePath = file.replace(/^\/papan\/?/, '')
      const pathResult = await filesApi.resolvePath(relativePath).catch(() => null)
      fileId = pathResult?.id || undefined
    }

    const fallbackLabel = file
      ? `Password for ${file.split('/').pop()}`
      : `Credential ${new Date().toISOString().slice(0, 10)}`

    // Server-side encryption: just POST the plaintext. Ownership is
    // scoped to (tenant, caller's active org email).
    const created = await keybookApi.createEntry(tenantId, {
      ownerType: 'email',
      ownerId: orgEmail.toLowerCase(),
      sourceLabel: label || fallbackLabel,
      credentialType: shape?.credentialType ?? 'password',
      fileId,
      data: { fields: values, credentialType: shape?.credentialType ?? 'password', ...(shape?.fieldsMeta ? { fieldsMeta: shape.fieldsMeta } : {}) },
    })
    return created.entryId
  } catch (err) {
    console.warn('[cred-prompt] Failed to save to keybook:', err)
    return undefined
  }
}

/** 'ok' once the entry holds the new values; 'changed' when the key changed since
 *  `expectedVersion` was read (409 KEYBOOK_ENTRY_CHANGED); 'failed' otherwise.
 *  Always against a version the prompt read — Update waits for it. */
async function updateKeybookEntry(entryId: string, data: Record<string, unknown>, expectedVersion: number): Promise<'ok' | 'changed' | 'failed'> {
  try {
    const { keybookApi } = await import('@papan/keybook/client')
    // the WHOLE payload (#1324): PATCH /value replaces it, so it is built from the entry as read
    await keybookApi.updateEntryValue(entryId, data, expectedVersion)
    return 'ok'
  } catch (err: any) {
    if (err?.code === 'KEYBOOK_ENTRY_CHANGED') return 'changed'
    console.warn('[cred-prompt] Failed to update keybook entry:', err)
    return 'failed'
  }
}
